import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,

  Text,
  Flex,
} from "@chakra-ui/react";
import { EmailIcon } from "@chakra-ui/icons";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { showToast } from "../utils/toastUtils";

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const auth = getAuth(); // Initialize Firebase Auth

  // Never surface raw Firebase error text to the user. The real error is
  // logged above for debugging; the toast only ever shows a generic message.
  const getResetErrorMessage = (errorCode?: string): string => {
    switch (errorCode) {
      case "auth/invalid-email":
        return "That email address looks invalid. Please check it and try again.";
      case "auth/user-not-found":
        return "If an account exists for that email, a password reset link is on its way.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a few minutes and try again.";
      case "auth/network-request-failed":
        return "We couldn't reach the server. Please check your connection and try again.";
      default:
        return "We couldn't send the reset email right now. Please try again.";
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      showToast({
        title: "Error",
        description: "Please enter your email address.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      await sendPasswordResetEmail(auth, email); // Use Firebase to send the reset email
      showToast({
        title: "Success",
        description: "Password reset email sent. Check your inbox.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      navigate("/login");
    } catch (error: any) {
      console.error("Error sending password reset email:", error);
      showToast({
        title: "Error",
        description: getResetErrorMessage(error?.code),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex
      height="100vh"
      overflow="hidden"
      align="center"
      justify="center"
      bg="#0B0B0F"
      width="100%"
      className="px-4"
    >
      <Box w="460px" mx="auto" className="p-[1.5px] rounded-2xl bg-gradient-to-br from-[#FBD28233] to-[#B5914333] shadow-[0_20px_60px_rgba(0,0,0,0.45)]">
        <Box borderRadius="2xl" textAlign={"center"} bg="#1A1C1F" className="relative overflow-hidden md:p-10 p-8">
          {/* Decorative corner glow */}
          <Box className="pointer-events-none" position="absolute" right="-60px" top="-60px" w="180px" h="180px" borderRadius="full" style={{ background: "radial-gradient(120px 120px at center, rgba(251,210,130,0.18), rgba(0,0,0,0))" }} />
          {/* Header Icon Badge */}
          <Box className="mx-auto -mt-2 mb-4" display="inline-flex" alignItems="center" justifyContent="center" w="12" h="12" borderRadius="full" bg="#2A3441" style={{ boxShadow: "inset 0 0 0 1px #2E2F33, 0 6px 16px rgba(0,0,0,0.35)" }}>
            <EmailIcon color="#FBD282" boxSize={5} />
          </Box>
        <Heading mb={2} className="text-3xl md:text-4xl font-extrabold tracking-tight">
          <Text
            as="span"
            bgClip="text"
            bgGradient="linear(to-r, #FBD282, #B59143)"
          >
            Forgot Password
          </Text>
        </Heading>
        <Text className="text-sm text-[#4A5463] mb-8">We'll send a reset link to your email.</Text>
        <VStack spacing={6} align="stretch">
          <FormControl isRequired>
            <FormLabel color="#B59143" className="text-sm font-semibold">Email</FormLabel>
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              borderRadius="lg"
              border="1px solid"
              borderColor="#2E2F33"
              bg="#2A3441"
              color="white"
              _placeholder={{ color: "#4A5463" }}
              _hover={{ borderColor: "#B59143" }}
              _focus={{ borderColor: "#B59143", boxShadow: "0 0 0 1px #B59143" }}
              className="h-12 text-sm"
            />
          </FormControl>

          <Button
            bgGradient="linear(to-r, #FBD282, #B59143)"
            color="white"
            fontWeight="bold"
            borderRadius="lg"
            onClick={handleForgotPassword}
            isLoading={loading}
            width="full"
            _hover={{ bgGradient: "linear(to-r, #E8C16C, #A7843D)", boxShadow: "0 4px 12px rgba(181, 145, 67, 0.35)" }}
            _active={{ transform: "translateY(0)" }}
            className="h-12 text-sm font-semibold tracking-wide rounded-xl transition-all duration-200 shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:shadow-[0_10px_24px_rgba(181,145,67,0.25)] focus:outline-none focus:ring-2 focus:ring-[#B59143]/50 active:scale-[.99]"
          >
            Send Reset Link
          </Button>
        </VStack>
        <Text position={"relative"} top={"16px"} fontSize="xs" color="rgba(255,255,255,0.85)" className="leading-relaxed">
          Redirect to login page :
          <Text
            as="span"
            ml={2}
            fontWeight="bold"
            cursor="pointer"
            color={"#B59143"}
            _hover={{ textDecoration: "none", color: "#A7843D" }}
            onClick={() => navigate("/login")}
            className="underline-offset-4"
          >
            Login
          </Text>
        </Text>
        </Box>
      </Box>
    </Flex>
  );
};

export default ForgotPassword;
